import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
`;

const ToolbarButton = styled.button<{ active?: boolean }>`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: ${props => props.active ? '#3b82f6' : 'white'};
  color: ${props => props.active ? 'white' : '#374151'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    background: ${props => props.active ? '#2563eb' : '#f3f4f6'};
    border-color: ${props => props.active ? '#2563eb' : '#9ca3af'};
  }
  
  &:active {
    background: ${props => props.active ? '#1d4ed8' : '#e5e7eb'};
  }
`;

const ToolbarDivider = styled.div`
  width: 1px;
  background: #d1d5db;
  margin: 0 4px;
`;

const EditorTextarea = styled.textarea`
  width: 100%;
  min-height: 300px;
  padding: 16px;
  border: none;
  outline: none;
  font-size: 14px;
  line-height: 1.6;
  font-family: inherit;
  resize: vertical;
  
  &:focus {
    outline: none;
  }
`;

const EditorDiv = styled.div`
  width: 100%;
  min-height: 300px;
  padding: 16px;
  border: none;
  outline: none;
  font-size: 14px;
  line-height: 1.6;
  font-family: inherit;
  overflow-y: auto;
  
  &:focus {
    outline: none;
    background: #fefefe;
  }
  
  &[contenteditable="true"] {
    cursor: text;
  }
  
  /* Placeholder styling for empty contentEditable */
  &:empty:before {
    content: attr(data-placeholder);
    color: #9ca3af;
    font-style: italic;
  }
  
  /* Style the HTML elements */
  strong, b {
    font-weight: 700;
  }
  
  em, i {
    font-style: italic;
  }
  
  u {
    text-decoration: underline;
  }
  
  h2 {
    font-size: 1.5em;
    font-weight: 600;
    margin: 12px 0;
  }
  
  h3 {
    font-size: 1.3em;
    font-weight: 600;
    margin: 10px 0;
  }
  
  ul {
    margin: 8px 0;
    padding-left: 24px;
  }
  
  ol {
    margin: 8px 0;
    padding-left: 24px;
  }
  
  li {
    margin: 4px 0;
  }
  
  p {
    margin: 8px 0;
  }
`;

const InlineImageUpload = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 16px;
  margin: 8px 0;
  background: #f9fafb;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
  }
`;

const UploadArea = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;
  
  &:hover {
    border-color: #3b82f6;
    background: #f8fafc;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 200px;
  border-radius: 8px;
  margin: 16px 0;
`;

const AltTextInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.primary {
    background: #3b82f6;
    color: white;
    
    &:hover {
      background: #2563eb;
    }
  }
  
  &.secondary {
    background: #6b7280;
    color: white;
    
    &:hover {
      background: #4b5563;
    }
  }
`;

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  uploadEndpoint?: string; // Optional - for image uploads
  showImageUpload?: boolean; // Whether to show image upload button
  minHeight?: string;
  maxHeight?: string; // Optional - for limiting height and enabling scroll
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Start writing...",
  uploadEndpoint,
  showImageUpload = false,
  minHeight = '300px',
  maxHeight
}) => {
  const [showImageUploadPanel, setShowImageUploadPanel] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [altText, setAltText] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const isUserTyping = useRef(false);

  // Set initial content only when value changes externally (not from user typing)
  useEffect(() => {
    const editor = editorRef.current;
    if (editor && !isUserTyping.current && value !== editor.innerHTML) {
      // Save cursor position
      const selection = window.getSelection();
      const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
      const cursorPosition = range ? range.startOffset : 0;
      
      editor.innerHTML = value || '';
      
      // Try to restore cursor position
      if (range && editor.firstChild) {
        try {
          const newRange = document.createRange();
          const textNode = editor.firstChild;
          const maxOffset = (textNode.textContent?.length || 0);
          newRange.setStart(textNode, Math.min(cursorPosition, maxOffset));
          newRange.collapse(true);
          selection?.removeAllRanges();
          selection?.addRange(newRange);
        } catch (e) {
          // Ignore cursor restoration errors
        }
      }
    }
    isUserTyping.current = false;
  }, [value]);

  const insertFormatting = (format: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    isUserTyping.current = true;
    
    // Use document.execCommand for proper formatting
    try {
      switch (format) {
        case 'bold':
          document.execCommand('bold', false);
          break;
        case 'italic':
          document.execCommand('italic', false);
          break;
        case 'underline':
          document.execCommand('underline', false);
          break;
        case 'heading':
          document.execCommand('formatBlock', false, 'h2');
          break;
        case 'list':
          document.execCommand('insertUnorderedList', false);
          break;
        default:
          break;
      }
      
      // Update the value with the current HTML content
      setTimeout(() => {
        onChange(editor.innerHTML);
      }, 0);
    } catch (error) {
      console.error('Formatting error:', error);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile || !uploadEndpoint) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await fetch(uploadEndpoint, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const data = await response.json();
      
      // Insert image placeholder in content
      const imagePlaceholder = `[IMAGE:${data.filename || data.fileName}]`;
      const editor = editorRef.current;
      if (editor) {
        // Insert at cursor position or at end
        const selection = window.getSelection();
        const range = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
        
        if (range) {
          range.deleteContents();
          const textNode = document.createTextNode(`\n${imagePlaceholder}\n`);
          range.insertNode(textNode);
          range.setStartAfter(textNode);
          range.setEndAfter(textNode);
          selection?.removeAllRanges();
          selection?.addRange(range);
        } else {
          editor.innerHTML += `\n${imagePlaceholder}\n`;
        }
        
        onChange(editor.innerHTML);
      }
      
      // Close upload form and reset
      setShowImageUploadPanel(false);
      setSelectedFile(null);
      setImagePreview(null);
      setAltText('');
      
    } catch (error) {
      console.error('Image upload failed:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image file must be less than 5MB');
      return;
    }

    setSelectedFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
      setShowImageUploadPanel(true);
    }
  };

  return (
    <EditorContainer>
      <Toolbar>
        <ToolbarButton onClick={() => insertFormatting('bold')} title="Bold (Ctrl+B)">
          <strong>B</strong> Bold
        </ToolbarButton>
        <ToolbarButton onClick={() => insertFormatting('italic')} title="Italic (Ctrl+I)">
          <em>I</em> Italic
        </ToolbarButton>
        <ToolbarButton onClick={() => insertFormatting('underline')} title="Underline">
          <u>U</u> Underline
        </ToolbarButton>
        
        <ToolbarDivider />
        
        <ToolbarButton onClick={() => insertFormatting('heading')} title="Heading">
          <strong>H</strong> Heading
        </ToolbarButton>
        <ToolbarButton onClick={() => insertFormatting('list')} title="Bullet List">
          📋 List
        </ToolbarButton>
        
        {showImageUpload && uploadEndpoint && (
          <>
            <ToolbarDivider />
            <ToolbarButton onClick={() => setShowImageUploadPanel(true)} title="Insert Image">
              📷 Insert Image
            </ToolbarButton>
          </>
        )}
      </Toolbar>
      
      <EditorDiv
        ref={editorRef}
        contentEditable={true}
        onInput={(e) => {
          isUserTyping.current = true;
          onChange(e.currentTarget.innerHTML);
        }}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
        style={{ 
          minHeight: minHeight,
          maxHeight: maxHeight || 'none',
          overflowY: maxHeight ? 'auto' : 'visible',
          position: 'relative'
        }}
      />

      {showImageUploadPanel && uploadEndpoint && (
        <InlineImageUpload>
          <h4 style={{ margin: '0 0 12px 0', color: '#1f2937', fontSize: '16px' }}>
            📷 Insert Image
          </h4>
          
          <UploadArea onClick={() => fileInputRef.current?.click()}>
            <div style={{ color: '#6b7280', marginBottom: '8px' }}>
              📁 Click to browse or drag & drop image file
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
              Supports: JPG, PNG, GIF • Max size: 5MB
            </div>
          </UploadArea>
          
          <FileInput
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
          />
          
          {imagePreview && (
            <ImagePreview src={imagePreview} alt="Preview" />
          )}
          
          <AltTextInput
            type="text"
            placeholder="Alt text for image (optional)"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
          />
          
          <ButtonGroup style={{ marginTop: '12px' }}>
            <Button
              className="secondary"
              onClick={() => {
                setShowImageUploadPanel(false);
                setSelectedFile(null);
                setImagePreview(null);
                setAltText('');
              }}
            >
              Cancel
            </Button>
            <Button
              className="primary"
              onClick={handleImageUpload}
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? 'Uploading...' : 'Insert Image'}
            </Button>
          </ButtonGroup>
        </InlineImageUpload>
      )}
      
      {/* Formatting Guide */}
      <div style={{
        padding: '8px 12px',
        background: '#f9fafb',
        borderTop: '1px solid #e5e7eb',
        fontSize: '11px',
        color: '#6b7280',
        display: 'flex',
        gap: '16px',
        flexWrap: 'wrap'
      }}>
        <span>📝 Formatting: &lt;strong&gt;bold&lt;/strong&gt; &lt;em&gt;italic&lt;/em&gt; &lt;u&gt;underline&lt;/u&gt;</span>
        <span>📋 Lists: &lt;ul&gt;&lt;li&gt;item&lt;/li&gt;&lt;/ul&gt;</span>
        <span>📌 Headings: &lt;h2&gt;Title&lt;/h2&gt;</span>
        {showImageUpload && <span>📷 Images: [IMAGE:filename.jpg]</span>}
      </div>
    </EditorContainer>
  );
};

export default RichTextEditor;

